import React, { useEffect, useRef, useState } from 'react';

// Shared helpers for the Planetary Power-Split Calculator's hand-drawn SVGs
// (ArchitectureDiagram, PlanetaryCutaway, LeverDiagram).
//
// Same two rendering rules as tyreSize/TyreDiagram.jsx:
//  1. Colours come from theme tokens through `style` (hsl(var(--token))); SVG presentation
//     attributes can't read CSS variables.
//  2. Any SVG element with dynamic children (text content, mapped/conditional children) is
//     built with React.createElement (`h`), not JSX: the dev-only visual-edits Babel plugin
//     wraps every {expr} child of a lowercase JSX element in an HTML <span>, which is invalid
//     inside <svg> and silently drops the content.

export const h = React.createElement;

export const tok = (name, alpha) => (alpha === undefined ? `hsl(var(${name}))` : `hsl(var(${name}) / ${alpha})`);

export const C = {
  fg: tok('--foreground'),
  muted: tok('--muted-foreground'),
  faint: tok('--muted-foreground', 0.35),
  card: tok('--card'),
  border: tok('--border'),
  mutedBg: tok('--muted'),
  primary: tok('--primary'),
  sun: tok('--ps-sun'),
  carrier: tok('--ps-carrier'),
  ring: tok('--ps-ring'),
  mg2: tok('--ps-mg2'),
  elec: tok('--ps-elec'),
};

export const MEMBER_COLOR = { sun: C.sun, carrier: C.carrier, ring: C.ring };

export const FONT = 'JetBrains Mono, ui-monospace, monospace';
export const HALO = { paintOrder: 'stroke', stroke: C.card, strokeWidth: 3, strokeLinejoin: 'round' };

export const n2 = (n) => Math.round(n * 100) / 100;

export function txt(key, x, y, content, { anchor = 'start', size = 11, weight = 400, color = C.fg, halo = false, baseline } = {}) {
  return h('text', {
    key,
    x: n2(x),
    y: n2(y),
    textAnchor: anchor,
    dominantBaseline: baseline,
    fontSize: size,
    fontWeight: weight,
    fontFamily: FONT,
    style: { fill: color, ...(halo ? HALO : {}) },
  }, content);
}

const num = (n, d) => Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
// Signed, with a true minus sign; "0" never shows a sign.
export const fmtSigned = (n, d) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const v = Number(n);
  const s = num(Math.abs(v), d);
  return v < 0 && s !== num(0, d) ? `−${s}` : s;
};
export const fmtRpm = (n) => `${fmtSigned(n, 0)} rpm`;
export const fmtNm = (n) => `${fmtSigned(n, Math.abs(n) >= 1000 ? 0 : 1)} N·m`;
export const fmtKw = (n) => `${fmtSigned(n, 2)} kW`;

export function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

// Width of a wrapper element, for picking the portrait (phone) or landscape diagram layout.
export function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// Tap / hover / focus selection shared by the diagrams. Hover wins over focus, which wins
// over a tap-pinned item. A tap clears hover and focus too: touch browsers emulate
// mouseenter without mouseleave, and tapping a focusable element also focuses it, so either
// would keep a toggled-off item lit.
export function useLinkedSelection() {
  const [sel, setSel] = useState({ hover: null, focus: null, pinned: null });
  const on = (kind, id) => setSel((prev) => {
    if (kind === 'hover') return { ...prev, hover: id };
    if (kind === 'leave') return { ...prev, hover: null };
    if (kind === 'focus') return { ...prev, focus: id };
    if (kind === 'blur') return { ...prev, focus: null };
    if (kind === 'tap') return { hover: null, focus: null, pinned: prev.pinned === id ? null : id };
    return prev;
  });
  return [sel.hover || sel.focus || sel.pinned, on, sel.pinned];
}
